import { getBagClient } from "../../../ecom-mybag/src/utils/bag-client";

export const getMinEddGetByDate = (shippingMethods) => {
  const getByDateTimestamps = shippingMethods
    .map((shippingMethod) => {
      if (shippingMethod?.getBy?.utc) {
        // convert utc to timestamp
        // e.g. 2020-10-15T05:32:20.442Z to 1602739940442
        return new Date(shippingMethod.getBy.utc).getTime();
      }
      return null;
    })
    .filter((timeStamp) => Number.isInteger(timeStamp));

  if (getByDateTimestamps.length) {
    // spread all the time stamps as parameter; find the minimum number, and convert back to UTC format
    return new Date(Math.min(...getByDateTimestamps)).toISOString();
  }

  return null;
};

// TODO: REMOVE THIS FUNCTION, THIS IS JUST TEMPORARY TO OVERCOME AN ISSUE OF ATG
export const removeTimeDecimals = (inTimeStamp) => {
  return inTimeStamp.replace(".000", "");
};

export const atgToEddShippingMethodMapping = {
  CanadaPost: "GROUND",
  FedExGround: "GROUND",
  FedExGroundCanada: "GROUND",
  LoyaltyMembershipExpressCA: "EXPRESS",
  LoyaltyMembershipExpressUS: "EXPRESS",
  FedExTwoDayCanadaFree: "EXPRESS",
  FedExTwoDayFree: "EXPRESS",
  FedExTwoDay: "EXPRESS",
  FedExTwoDayCanada: "EXPRESS",
  FedExOvernightCanadaFree: "PRIORITY",
  FedExOvernightFree: "PRIORITY",
  FedExOvernight: "PRIORITY",
  FedExOvernightCanada: "PRIORITY"
};

// usage: getTimeDiffInMinutes("2020-09-19T10:41:08Z");
export function getTimeDiffInMinutes(utcDateTime) {
  // internal function to convert date time to timestamp
  const convertToTimeStamp = (dateTime) => dateTime.getTime() / 1000;

  const futureTime = new Date(utcDateTime);
  const futureTimeStamp = convertToTimeStamp(futureTime);

  const currentTime = new Date();
  const currentTimeStamp = convertToTimeStamp(currentTime);

  return Math.floor((futureTimeStamp - currentTimeStamp) / 60);
}

// for displaying cut off time
export function convertMinsToHumanReadable(minutes, localeString) {
  // const for time string
  const timeString = !localeString && {
    HOURS: "hrs",
    HOUR: "hr",
    MINUTES: "mins",
    MINUTE: "min"
  };

  // constant for time checking
  const TIME24HOURS = 24 * 60;
  const TIME2HOURS = 60 * 2;
  const TIME1HOUR = 60;

  let humanReadable = null;
  if (minutes >= TIME24HOURS) {
    humanReadable = null;
  } else if (minutes >= TIME2HOURS && minutes < TIME24HOURS) {
    humanReadable = `${Math.floor(minutes / 60)} ${timeString.HOURS}`;
  } else if (minutes >= TIME1HOUR && minutes < TIME2HOURS) {
    humanReadable = `1 ${timeString.HOUR}`;
  } else if (minutes > 1 && minutes < TIME1HOUR) {
    humanReadable = `${minutes} ${timeString.MINUTES}`;
  } else if (minutes > 0 && minutes === 1) {
    humanReadable = `1 ${timeString.MINUTE}`;
  }

  return humanReadable;
}

// Helper function to convert UTC date string to local timezone
export const convertUTCDateToLocal = (utcDateString) => {
  // Handle null, undefined, or empty string inputs
  if (!utcDateString || typeof utcDateString !== "string") {
    return utcDateString;
  }

  try {
    const date = new Date(utcDateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return utcDateString; // Return original if invalid
    }
    return date;
  } catch (error) {
    return utcDateString;
  }
};

export const formatDateToShortString = (date) => {
  const dateObj = new Date(date);

  // Validate the date
  if (isNaN(dateObj.getTime())) {
    throw new Error("Invalid date provided");
  }
  // Get day of week abbreviation
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dayAbbr = dayNames[dateObj.getDay()];

  // Get month abbreviation
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
  ];
  const monthAbbr = monthNames[dateObj.getMonth()];

  // Get day of month
  const dayOfMonth = dateObj.getDate();

  return `${dayAbbr}, ${monthAbbr} ${dayOfMonth}`;
};

export const transformSingleEstimatedDeliveryDate = (singleEddData) => {
  // Handle eddDisplayString conversion based on deliveryType
  let processedEddDisplayString = singleEddData.eddDisplayString;

  if (singleEddData.deliveryType === "date" && singleEddData.eddDisplayString) {
    // For date delivery type, convert UTC date string to browser local timezone
    processedEddDisplayString = formatDateToShortString(
      convertUTCDateToLocal(singleEddData.eddDisplayString)
    );
  }

  return {
    _originalData: singleEddData,
    orderCutOffDate: convertMinsToHumanReadable(
      getTimeDiffInMinutes(singleEddData.orderCutOffDate)
    ),
    orderCutOffDateUTC: singleEddData.orderCutOffDate,
    estimatedDeliveryDate: removeTimeDecimals(
      singleEddData.estimatedDeliveryDate
    ),
    eddDisplayString: processedEddDisplayString,
    validUntilDate: singleEddData.validUntilDate
  };
};

export const transformEstimatedDeliveryDateData = (country, zipCode, data) => {
  const transformedData = {};
  data.forEach((d) => {
    transformedData[d.shipMethod] = transformSingleEstimatedDeliveryDate(d);
  });
  return {
    country,
    zipCode,
    data: transformedData
  };
};

export const getRequestedEddDates = (
  eddShippingMethods,
  atgShippingMethods
) => {
  // clone incoming eddShippingMethods data to avoid changing the original data
  let cloneEddShippingMethods = {
    ...eddShippingMethods,
    data: {
      ...eddShippingMethods.data
    }
  };

  // loop through all the ATG shipping methods
  atgShippingMethods.forEach((atgSm) => {
    // get edd shipping method name
    const eddSm = atgToEddShippingMethodMapping[atgSm?.code];
    // check and see if there is no requested EDD solution for the shipping method
    if (eddSm && !eddShippingMethods?.data?.[eddSm]) {
      // if no solution, then check if ATG set getBy and orderBy
      if (atgSm?.getBy && atgSm?.orderBy) {
        // if so, create the shape of the data that fits into the transformation
        const transformatedData = transformSingleEstimatedDeliveryDate({
          shipMethod: eddSm,
          orderCutOffDate: atgSm?.orderBy?.utc,
          estimatedDeliveryDate: atgSm?.getBy?.utc,
          eddDisplayString: atgSm?.getBy?.eddDisplayString,
          source: "ATG"
        });
        // extend the object to become the edd data of that particular shipping method
        cloneEddShippingMethods = {
          data: {
            ...cloneEddShippingMethods.data,
            [eddSm]: transformatedData
          }
        };
      }
      // else no extension to the object, that means it will fall back to ATG eddText
    }
    // else it has solution, and just use the EDD comes from the API
    // set isRequestedEdd
    if (cloneEddShippingMethods?.data?.[eddSm]) {
      cloneEddShippingMethods.data[eddSm].isRequestedEdd = !!atgSm?.getBy;
    }
  });

  // return the modified object
  return cloneEddShippingMethods;
};

export const transformRequestedEddData = (
  country,
  zipCode,
  eddData,
  atgShippingMethods
) => {
  const eddShippingMethods = transformEstimatedDeliveryDateData(
    country,
    zipCode,
    eddData
  );
  const eddDatesData = getRequestedEddDates(
    eddShippingMethods,
    atgShippingMethods
  );
  return eddDatesData;
};

export const getEstimatedDeliveryDateWithGraphQL = async (
  graphql,
  variables
) => {
  const getEstimatedDeliveryDateMutation = `
    query getEstimatedDeliveryDate(
      $zipCode: String!,
      $bagId: String,
      $requestedDeliveryDate: String
    ) {
      getEstimatedDeliveryDate(
        zipCode: $zipCode,
        bagId: $bagId,
        requestedDeliveryDate: $requestedDeliveryDate
      ) {
        shipMethod,
        orderCutOffDate,
        estimatedDeliveryDate,
        eddDisplayString
      }
    }
  `;

  const bagClient = getBagClient();
  const promise = await bagClient.getEstimatedDeliveryDate({
    variables
  });

  return promise;
};

export function sanitizePostalZip(inPostalZip) {
  return inPostalZip
    .replace(/[^0-9a-z-]/gi, "") // clean up any invalid chars
    .replace(/^([\d]{5})[ -]?([\d]{4})?$/, "$1") // sanitize US zip code
    .replace(/^([A-Za-z\d]{3})[ -]?([A-Za-z\d]{3})$/, "$1 $2") // sanitize CA postal code
    .toUpperCase()
    .trim();
}

export const mappedShippingMethods = (shippingMethods, eddData) => {
  if (!shippingMethods) {
    return [];
  }
  return shippingMethods.map((sm) => {
    const atgSmCode = sm?.code;
    const mappedSmCode = atgToEddShippingMethodMapping?.[atgSmCode];
    const eddHasData = eddData?.data?.[mappedSmCode];
    const timeStamp = new Date(eddHasData?.estimatedDeliveryDate).getTime();

    // tenYearsFromToday is for making the date 10 years from todays date. This will
    // help instance where there may be an undefined or NAN value; creating a value
    // that is later than the other available options. The number at the end is ten
    // years in milliseconds
    const todaysDate = new Date();
    const tenYearsFromToday = todaysDate.getTime() + 315569520000;

    return {
      ...sm,
      eddData: {
        ...eddHasData,
        estimatedDeliveryDateTimestamp: Number.isInteger(timeStamp)
          ? timeStamp
          : tenYearsFromToday
      },
      isTimeValid: Number.isInteger(timeStamp)
    };
  });
};

export const getFastestShippingMethod = (option1, option2, option3) => {
  const option1Timestamp = option1?.eddData?.estimatedDeliveryDateTimestamp;
  const option2Timestamp = option2?.eddData?.estimatedDeliveryDateTimestamp;
  const option3Timestamp = option3?.eddData?.estimatedDeliveryDateTimestamp;

  if (!option1Timestamp && !option2Timestamp && !option3Timestamp) {
    return null;
  }

  const hasAdditionalOptions = !!option2Timestamp || !!option3Timestamp;
  const freeShippingIsFastest =
    !!option1Timestamp &&
    option1Timestamp <= option2Timestamp &&
    option1Timestamp <= option3Timestamp;

  // if option 1 (free shipping option) is faster than both of the other options, or we don't have other options, return option1
  if (freeShippingIsFastest || !hasAdditionalOptions) {
    return option1;
  }

  // if free shipping isn't fastest, return which ever remaining option is fastest
  return option3Timestamp < option2Timestamp ? option3 : option2;
};

export const freeAndFastestShippingOptions = (eddData, shippingMethods) => {
  const hasEddData = Object.keys(eddData?.data || {}).length > 0;

  const [freeShippingOption, option2, option3] = mappedShippingMethods(
    shippingMethods,
    eddData
  );

  // Detect if we're using new EDD format (has deliveryType and eddDisplayString directly on shipping methods)
  const isNewEddFormat = shippingMethods.some(
    (method) => method.deliveryType && method.eddDisplayString
  );

  const requestedDeliveryDate = shippingMethods.length
    ? getMinEddGetByDate(shippingMethods)
    : null;

  // fastest can only be determined if eddData has been returned
  const fastestShippingOption = hasEddData
    ? getFastestShippingMethod(freeShippingOption, option2, option3)
    : null;

  // will be false if fastestShippingOption is null
  const freeOptionIsFastest =
    freeShippingOption?.code === fastestShippingOption?.code;

  // For new EDD format, check display string differently
  let isMissingFastestDisplayString;
  if (isNewEddFormat) {
    // For new format, check if the fastest shipping method has eddDisplayString in the original data
    const fastestOriginalMethod = shippingMethods.find(
      (method) => method.code === fastestShippingOption?.code
    );
    isMissingFastestDisplayString =
      hasEddData && !fastestOriginalMethod?.eddDisplayString;
  } else {
    // TODO-OPAL cleanup else logic when opal live
    // Legacy logic for old format
    isMissingFastestDisplayString =
      hasEddData &&
      !requestedDeliveryDate &&
      !fastestShippingOption?.eddData?.eddDisplayString;
  }

  // do not display fastest option if free option is faster, or if we have eddData, but are missing a delivery date string
  const shouldDisplayFastestShipping =
    !freeOptionIsFastest && !isMissingFastestDisplayString;

  return {
    shouldDisplayFastestShipping,
    freeShippingOption,
    fastestShippingOption
  };
};

/**
 * Get formatted EDD display string based on delivery type and feature flags
 * @param {Object} selectedMethod - The selected shipping method
 * @param {Object} eddData - The EDD data containing shipping method information
 * @param {string} estimatedDeliveryDate - ISO date string from the bag
 * @param {string} fallbackDisplayString - Fallback display string (PST format)
 * @param {boolean} isEnhancedEddEnabled - Whether enhanced EDD formatting is enabled
 * @returns {string} Formatted EDD display string
 */
export const getFormattedEddDisplayString = ({
  selectedMethod,
  eddData,
  estimatedDeliveryDate,
  fallbackDisplayString,
  isEnhancedEddEnabled
}) => {
  // If enhanced EDD is not enabled, return fallback immediately without processing
  if (!isEnhancedEddEnabled) {
    return fallbackDisplayString;
  }

  // Determine delivery type from eddData using selectedMethod
  let deliveryType = null;

  if (eddData?.data && selectedMethod?.code) {
    // Map ATG shipping method code to EDD shipping method name
    const eddShippingMethodName =
      atgToEddShippingMethodMapping[selectedMethod.code];

    if (eddShippingMethodName && eddData.data[eddShippingMethodName]) {
      // Get deliveryType from _originalData
      deliveryType =
        eddData.data[eddShippingMethodName]._originalData?.deliveryType;
    }
  }

  const isDateDelivery = deliveryType === "date";

  if (estimatedDeliveryDate && isDateDelivery) {
    const localDate = convertUTCDateToLocal(estimatedDeliveryDate);
    return formatDateToShortString(localDate);
  }

  // Bag has no estimatedDeliveryDate (e.g. cleared after bag edit) but eddData
  // from getShippingMethodsWithEDD has the date — use it directly
  if (!estimatedDeliveryDate && isDateDelivery && eddData?.data) {
    const eddShippingMethodName =
      atgToEddShippingMethodMapping[selectedMethod?.code];
    const methodEdd = eddData.data[eddShippingMethodName];
    if (methodEdd?.eddDisplayString) {
      return methodEdd.eddDisplayString;
    }
  }

  // Enhanced EDD without eddData (OPAL format for declutter flow)
  // Always convert estimatedDeliveryDate to user's local timezone
  // Note: fallbackDisplayString from backend is PST-formatted, so we ignore it for date deliveries
  if (!eddData && estimatedDeliveryDate && fallbackDisplayString) {
    const fallbackFormat = identifyDateFormat(fallbackDisplayString);

    // For date-based deliveries, convert to user's local timezone
    if (fallbackFormat === "date") {
      const localDate = convertUTCDateToLocal(estimatedDeliveryDate);
      return formatDateToShortString(localDate);
    }

    // For range-based deliveries, use the range text from estimatedDeliveryText
    if (fallbackFormat === "range") {
      return selectedMethod?.estimatedDeliveryText || fallbackDisplayString;
    }
  }

  return selectedMethod?.estimatedDeliveryText || fallbackDisplayString;
};

export const identifyDateFormat = (dateString) => {
  // Handle null, undefined, or non-string inputs
  if (!dateString || typeof dateString !== "string") {
    return "unknown";
  }

  // Check for range format (e.g., "5-6 days", "2-3 weeks")
  const rangePattern = /^\d+-\d+\s+(day|days|week|weeks|month|months)$/i;

  // Check for readable date format (e.g., "Thu, Nov 27")
  const readablePattern = /^[A-Za-z]{3},\s+[A-Za-z]{3}\s+\d{1,2}$/;

  if (rangePattern.test(dateString.trim())) {
    return "range";
  } else if (readablePattern.test(dateString.trim())) {
    return "date";
  } else {
    return "unknown";
  }
};
